import { useState, type FormEvent, useEffect } from 'react';

import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { normalizePhoneInput } from '../../lib/phone-input';
import { useToast } from '../../lib/toast';
import { useDialysisLookup, useUpdateDialysisAdmission } from './dialysis.queries';
import {
  AdmissionInfoFields,
  FieldLabel,
  LookupSelect,
  PatientDemographicFields,
  PatientIdentityFields,
  ResponsiblePersonFields,
} from './dialysis.shared';
import { toDateTimeInput } from '../ipd-daycare/ipd-daycare.utils';
import type { DialysisAdmission, PatientFormFields, ResponsiblePersonFormFields, UpdateDialysisAdmissionPayload } from './types';

interface AdmissionInfoState {
  underDoctor: string | null;
  dialysisMachine: string | null;
  startTime: string;
  endTime: string;
  bedCategory: string | null;
  bed: string | null;
  referredBy: string | null;
}

const patientFieldsFromAdmission = (row: DialysisAdmission): PatientFormFields => ({
  patientId: row.patient.id,
  uhid: row.patientSnapshot.uhid,
  name: row.patientSnapshot.name,
  phone: row.patientSnapshot.phone ? normalizePhoneInput(row.patientSnapshot.phone) : null,
  gender: row.patientSnapshot.gender,
  maritalStatus: row.patientSnapshot.maritalStatus,
  guardianName: row.patientSnapshot.guardianName,
  relation: row.patientSnapshot.relation,
  guardianContactNo: row.patientSnapshot.guardianContactNo ? normalizePhoneInput(row.patientSnapshot.guardianContactNo) : null,
  dateOfBirth: row.patientSnapshot.dateOfBirth,
  ageYears: row.patientSnapshot.ageYears,
  ageMonths: row.patientSnapshot.ageMonths,
  ageDays: row.patientSnapshot.ageDays,
  address: row.patientSnapshot.address,
  state: row.patientSnapshot.state,
  district: row.patientSnapshot.district,
  pinCode: row.patientSnapshot.pinCode,
  aadhaar: row.patientSnapshot.aadhaar,
});

const admissionInfoFromAdmission = (row: DialysisAdmission): AdmissionInfoState => ({
  underDoctor: row.underDoctor?.id ?? null,
  dialysisMachine: row.dialysisMachine.id,
  startTime: row.startTime,
  endTime: row.endTime,
  bedCategory: row.bedCategory.id,
  bed: row.bed.id,
  referredBy: row.referredBy.id,
});

export const DialysisEditAdmissionForm = ({ admission, onSaved }: { admission: DialysisAdmission; onSaved: () => void }) => {
  const { showToast } = useToast();
  const mutation = useUpdateDialysisAdmission();

  useEffect(() => {
    if (mutation.isSuccess) {
      const timer = setTimeout(() => {
        onSaved();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [mutation.isSuccess, onSaved]);

  const [admissionDate, setAdmissionDate] = useState(() => toDateTimeInput(admission.admissionDate));
  const [insuranceType, setInsuranceType] = useState(admission.insuranceType);
  const [insuranceNumber, setInsuranceNumber] = useState(admission.insuranceNumber ?? '');
  const [patient, setPatient] = useState<PatientFormFields>(() => patientFieldsFromAdmission(admission));
  const [admissionInfo, setAdmissionInfo] = useState<AdmissionInfoState>(() => admissionInfoFromAdmission(admission));
  const [responsiblePerson, setResponsiblePerson] = useState<ResponsiblePersonFormFields>(() => ({
    name: admission.responsiblePerson.name ?? '',
    relation: admission.responsiblePerson.relation ?? '',
    phone: admission.responsiblePerson.phone ? normalizePhoneInput(admission.responsiblePerson.phone) : '',
    age: admission.responsiblePerson.age,
    address: admission.responsiblePerson.address ?? '',
  }));

  const states = useDialysisLookup('states');
  const districts = useDialysisLookup('districts', '', { state: patient.state ?? '' });
  const dialysisMachines = useDialysisLookup('dialysis-machines');
  const referrals = useDialysisLookup('referrals');
  const tpas = useDialysisLookup('tpas');
  const tpaOptions = tpas.data ?? [];
  const patientTypeOptions = tpaOptions.some((item) => item.label === insuranceType) || !insuranceType
    ? tpaOptions
    : [{ id: `current-${insuranceType}`, label: insuranceType }, ...tpaOptions];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (admissionInfo.endTime <= admissionInfo.startTime) {
      showToast('End Time must be later than Start Time.', 'error');
      return;
    }
    const payload: UpdateDialysisAdmissionPayload = {      name: patient.name,
      phone: patient.phone,
      gender: patient.gender,
      maritalStatus: patient.maritalStatus,
      guardianName: patient.guardianName,
      relation: patient.relation,
      guardianContactNo: patient.guardianContactNo,
      dateOfBirth: patient.dateOfBirth,
      ageYears: patient.ageYears,
      ageMonths: patient.ageMonths,
      ageDays: patient.ageDays,
      address: patient.address,
      state: patient.state,
      district: patient.district,
      pinCode: patient.pinCode,
      aadhaar: patient.aadhaar,
      admissionDate,
      insuranceType,
      insuranceNumber: insuranceNumber || null,
      dialysisMachine: admissionInfo.dialysisMachine ?? '',
      startTime: admissionInfo.startTime,
      endTime: admissionInfo.endTime,
      referredBy: admissionInfo.referredBy ?? '',
      responsiblePerson,
    };
    void confirmUpdate('this dialysis admission').then((confirmed) => {
      if (!confirmed) return;
      mutation.mutate({ id: admission.id, payload }, {
        onSuccess: () => showToast('Dialysis admission has been updated successfully.'),
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'Dialysis admission could not be saved.', 'error'),
      });
    });
  };

  const busy = mutation.isPending;

  return (
    <form className="ipd-daycare-registration-card" onSubmit={submit}>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>Edit Admission — {admission.patientSnapshot.name}</span></div>
        <p>Dialysis No {admission.dialysisNumber} · Under Doctor {doctorNameOrDash(admission.underDoctor?.name)} (not editable) · Bed {admission.bed.name} (change via Bed Transfer)</p>
      </header>

      <section className="ipd-daycare-section">
        <div className="ipd-daycare-top-row">
          <label><FieldLabel required>Admission Date &amp; time</FieldLabel><input type="datetime-local" value={admissionDate} onChange={(event) => setAdmissionDate(event.target.value)} required /></label>
          <label><FieldLabel required>Dialysis Machine</FieldLabel><LookupSelect id="dialysis-machine-top" value={admissionInfo.dialysisMachine ?? ''} options={dialysisMachines.data} placeholder="Select Machine" onChange={(dialysisMachine) => setAdmissionInfo((current) => ({ ...current, dialysisMachine: dialysisMachine || null }))} /></label>
          <label><FieldLabel required>Patient Type</FieldLabel><select value={insuranceType} onChange={(event) => setInsuranceType(event.target.value)} required>{patientTypeOptions.map((item) => <option key={item.id} value={item.label}>{item.label}</option>)}</select></label>
          <label><FieldLabel>Insurance No.</FieldLabel><input value={insuranceNumber} onChange={(event) => setInsuranceNumber(event.target.value)} maxLength={100} /></label>
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>Patient Details</h2>
        <div className="ipd-daycare-patient-row1">
          <PatientIdentityFields value={patient} onChange={setPatient} />
        </div>
        <div className="ipd-daycare-patient-row2">
          <PatientDemographicFields value={patient} onChange={setPatient} states={states.data} districts={districts.data} />
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>Dialysis Admission Information</h2>
        <div className="ipd-daycare-admission-row">
          <AdmissionInfoFields
            value={admissionInfo}
            onChange={setAdmissionInfo}
            dialysisMachines={dialysisMachines.data}
            referrals={referrals.data}
            hideMachine={true}
            lockedDoctorAndBed={{ underDoctorName: doctorNameOrDash(admission.underDoctor?.name), bedCategoryName: admission.bedCategory.name, bedName: admission.bed.name }}
          />
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>Responsible Person Details</h2>
        <div className="ipd-daycare-responsible-row">
          <ResponsiblePersonFields value={responsiblePerson} onChange={setResponsiblePerson} />
        </div>
      </section>

      <div className="ipd-daycare-form-actions"><button className="button button-primary" type="submit" disabled={busy}>{busy ? 'Saving...' : 'Save Changes'}</button></div>
    </form>
  );
};
